import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { mascotaService } from '../services/mascotaService'
import { usuarioService } from '../services/usuarioService'
import { mascotaCondicionService } from '../services/mascotaCondicionService'
import { condicionSaludService } from '../services/condicionSaludService'
import { veterinarioService } from '../services/veterinarioService'
import { registroPesoService } from '../services/registroPesoService'
import { accesoMascotasService } from '../services/accesoMascotasService'
import Table, { TableHeader, TableBody, TableRow, TableCell, TableHead } from '../components/ui/Table'
import Button from '../components/ui/Button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import ActionsMenu from '../components/ui/ActionsMenu'
import EmptyState from '../components/ui/EmptyState'
import LoadingState from '../components/ui/LoadingState'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Listbox from '../components/ui/Listbox'
import Disclosure from '../components/ui/Disclosure'
import Textarea from '../components/ui/Textarea'
import Tabs, { TabList, TabItem, TabPanels, TabPanel } from '../components/ui/Tabs'
import { 
  Heart, Plus, Search, Edit, Trash2, Eye, RefreshCw, AlertCircle, Calendar, Scale, User, Filter, X, FileText, Activity, Stethoscope,
  Skull, Archive, RotateCcw, QrCode, Copy, Clock, Link2, Share2, Mail, Phone, CheckCircle2
} from 'lucide-react'
import Avatar from '../components/ui/Avatar'
import { labelEstadoMascota } from '../utils/mascotaLabels'

function labelTipoRaza(t) {
  if (!t) return ''
  const m = { pura: 'Pura', cruza: 'Cruza', mixto: 'Mixto' }
  return m[t] || t
}

function formatOrigenMascota(m) {
  const o = m?.origen || m?.origenMascota
  if (!o) return null
  return o === 'Comprado' ? 'Comprado' : o === 'Adoptado' ? 'Adoptado' : o
}

export default function Mascotas() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isDueno, isVeterinario, isAdmin, isSuperAdmin, canCreateMascota } = usePermissions()
  const [mascotas, setMascotas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    search: '',
    especie: '',
    estado: '',
    tipo_raza: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [duenos, setDuenos] = useState([])
  const [loadingDuenos, setLoadingDuenos] = useState(false)
  const [formData, setFormData] = useState({
    nombre: '',
    especie: '',
    raza: '',
    tipo_raza: '',
    fecha_nacimiento: '',
    sexo: '',
    color: '',
    peso: '',
    altura: '',
    microchip: '',
    observaciones: '',
    origen: '',
    criadero: '',
    fecha_adopcion: '',
    id_dueño: isDueno ? user?.idUsuario : ''
  })
  const [submitting, setSubmitting] = useState(false)
  
  // Estados para condiciones de salud
  const [showCondicionesModal, setShowCondicionesModal] = useState(false)
  const [selectedMascota, setSelectedMascota] = useState(null)
  const [condicionesMascota, setCondicionesMascota] = useState([])
  const [loadingCondiciones, setLoadingCondiciones] = useState(false)
  const [catalogoCondiciones, setCatalogoCondiciones] = useState([])
  const [loadingCatalogo, setLoadingCatalogo] = useState(false)
  const [veterinarios, setVeterinarios] = useState([])
  const [showAddCondicionModal, setShowAddCondicionModal] = useState(false)
  const [condicionFormData, setCondicionFormData] = useState({
    id_condicion_salud: '',
    fecha_diagnostico: new Date().toISOString().split('T')[0],
    id_veterinario: '',
    id_ficha_clinica: '',
    severidad: 'MODERADA',
    estado: 'EN_TRATAMIENTO',
    notas: ''
  })
  const [submittingCondicion, setSubmittingCondicion] = useState(false)
  const [editingCondicion, setEditingCondicion] = useState(null)

  // Estados para registros de peso
  const [showRegistrosPesoModal, setShowRegistrosPesoModal] = useState(false)
  const [registrosPeso, setRegistrosPeso] = useState([])
  const [loadingRegistrosPeso, setLoadingRegistrosPeso] = useState(false)
  const [showAddRegistroPesoModal, setShowAddRegistroPesoModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [mascotaDetail, setMascotaDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [detailExtrasLoading, setDetailExtrasLoading] = useState(false)
  const [detailRegistrosPeso, setDetailRegistrosPeso] = useState([])
  const [detailCondiciones, setDetailCondiciones] = useState([])
  const [registroPesoFormData, setRegistroPesoFormData] = useState({
    peso: '',
    fecha_registro: new Date().toISOString().split('T')[0],
    tipo_registro: 'CASA',
    observaciones: ''
  })
  const [submittingRegistroPeso, setSubmittingRegistroPeso] = useState(false)
  const [editingRegistroPeso, setEditingRegistroPeso] = useState(null)
  const [mascotaAccion, setMascotaAccion] = useState(null)
  const [showFallecidaModal, setShowFallecidaModal] = useState(false)
  const [fallecidaFormData, setFallecidaFormData] = useState({
    fecha_fallecimiento: '',
    motivo_fallecimiento: ''
  })
  const [processingAction, setProcessingAction] = useState(false)

  // Compartir acceso temporal (QR/código) - solo DUEÑO
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareMascota, setShareMascota] = useState(null)
  const [shareForm, setShareForm] = useState({
    alcance: 'HISTORIAL_COMPLETO',
    duracion_minutos: 60,
  })
  const [shareResult, setShareResult] = useState(null)
  const [sharing, setSharing] = useState(false)
  const [shareError, setShareError] = useState(null)
  const [showPostulacionesModal, setShowPostulacionesModal] = useState(false)
  const [postulacionesMascota, setPostulacionesMascota] = useState([])
  const [loadingPostulaciones, setLoadingPostulaciones] = useState(false)
  const [resolvingPostulacionId, setResolvingPostulacionId] = useState(null)
  const [showPostulacionesGlobalModal, setShowPostulacionesGlobalModal] = useState(false)
  const [postulacionesGlobales, setPostulacionesGlobales] = useState([])
  const [loadingPostulacionesGlobales, setLoadingPostulacionesGlobales] = useState(false)
  const [filtrosPostulaciones, setFiltrosPostulaciones] = useState({
    estado: '',
    fecha_desde: '',
    fecha_hasta: '',
  })

  useEffect(() => {
    if (user) {
      fetchMascotas()
      if (isAdmin) {
        fetchDuenos()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current_page, filtros.search, filtros.especie, filtros.estado, filtros.tipo_raza, isAdmin])

  const fetchMascotas = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.search && { search: filtros.search }),
        ...(filtros.especie && { especie: filtros.especie }),
        ...(filtros.estado && { estado: filtros.estado }),
        ...(filtros.tipo_raza && { tipo_raza: filtros.tipo_raza })
      }

      const response = await mascotaService.getAll(params)
      
      if (response.success) {
        // La respuesta tiene estructura: { success: true, data: { data: [...], current_page, ... } }
        const paginatedData = response.data
        
        if (paginatedData && paginatedData.data) {
          // Respuesta paginada
          setMascotas(paginatedData.data)
          setPagination({
            current_page: paginatedData.current_page || 1,
            per_page: paginatedData.per_page || 15,
            total: paginatedData.total || 0,
            last_page: paginatedData.last_page || 1
          })
        } else if (Array.isArray(paginatedData)) {
          // Respuesta directa como array
          setMascotas(paginatedData)
          setPagination({
            current_page: 1,
            per_page: paginatedData.length,
            total: paginatedData.length,
            last_page: 1
          })
        } else if (Array.isArray(response.data)) {
          // Fallback: data es directamente un array
          setMascotas(response.data)
          setPagination({
            current_page: 1,
            per_page: response.data.length,
            total: response.data.length,
            last_page: 1
          })
        } else {
          setMascotas([])
        }
      } else {
        setError(response.error || 'Error al cargar mascotas')
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Error de conexión'
      setError(errorMessage)
      
      // Si es 401, el interceptor ya maneja el logout
      if (err.response?.status !== 401) {
        setMascotas([])
      }
    } finally {
      setLoading(false)
    }
  }

  const openShareModal = (mascota) => {
    setShareMascota(mascota)
    setShareForm({ alcance: 'HISTORIAL_COMPLETO', duracion_minutos: 60 })
    setShareResult(null)
    setShareError(null)
    setShowShareModal(true)
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch (e) {
      // noop
    }
  }

  const getShareUrl = () => {
    if (!shareResult) return ''
    if (shareResult.share_url) return shareResult.share_url
    const origin = window.location.origin
    return `${origin}/acceso-mascotas?token=${encodeURIComponent(shareResult.token)}`
  }

  const getQrImageUrl = () => {
    const url = getShareUrl()
    if (!url) return ''
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`
  }

  const handleNativeShare = async () => {
    const url = getShareUrl()
    if (!url) return
    const title = `Acceso temporal de ${shareMascota?.nombre || 'mascota'}`
    const text = `Acceso temporal (${shareForm.alcance === 'BASICO' ? 'básico' : 'historial completo'})`
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url })
      } else {
        await copyToClipboard(url)
      }
    } catch (_) {
      // noop
    }
  }

  const handleGenerateShare = async () => {
    if (!shareMascota) return
    try {
      setSharing(true)
      setShareError(null)
      setShareResult(null)

      const idMascota = shareMascota.id_mascota || shareMascota.idMascota
      const payload = {
        mascotas: [idMascota],
        alcance: shareForm.alcance,
        duracion_minutos: Number(shareForm.duracion_minutos) || 60,
      }

      const resp = await accesoMascotasService.create(payload)
      if (resp.success) {
        setShareResult(resp.data)
      } else {
        setShareError(resp.error || 'No se pudo generar el acceso')
      }
    } catch (err) {
      setShareError(err.response?.data?.error || err.message || 'Error al generar el acceso')
    } finally {
      setSharing(false)
    }
  }

  const handleRevokeShare = async () => {
    if (!shareResult?.id_acceso) return
    try {
      setSharing(true)
      setShareError(null)
      const resp = await accesoMascotasService.revocar(shareResult.id_acceso)
      if (resp.success) {
        setShareResult(null)
      } else {
        setShareError(resp.error || 'No se pudo revocar el acceso')
      }
    } catch (err) {
      setShareError(err.response?.data?.error || err.message || 'Error al revocar acceso')
    } finally {
      setSharing(false)
    }
  }

  const openPostulacionesModal = async (mascota) => {
    try {
      const idMascota = mascota?.id_mascota || mascota?.idMascota
      if (!idMascota) return
      setSelectedMascota(mascota)
      setShowPostulacionesModal(true)
      setLoadingPostulaciones(true)
      setError(null)
      const resp = await accesoMascotasService.listarPostulaciones({ id_mascota: idMascota })
      if (resp?.success) {
        setPostulacionesMascota(Array.isArray(resp.data) ? resp.data : [])
      } else {
        setError(resp?.error || 'No se pudieron cargar las postulaciones')
        setPostulacionesMascota([])
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al cargar postulaciones')
      setPostulacionesMascota([])
    } finally {
      setLoadingPostulaciones(false)
    }
  }

  const resolverPostulacion = async (postulacion, accion) => {
    const idPostulacion = postulacion?.id_postulacion
    if (!idPostulacion) return
    try {
      setResolvingPostulacionId(idPostulacion)
      setError(null)
      const resp = accion === 'aprobar'
        ? await accesoMascotasService.aprobarPostulacion(idPostulacion)
        : await accesoMascotasService.rechazarPostulacion(idPostulacion)

      if (!resp?.success) {
        setError(resp?.error || 'No se pudo resolver la postulacion')
        return
      }

      if (selectedMascota) {
        await openPostulacionesModal(selectedMascota)
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al resolver postulacion')
    } finally {
      setResolvingPostulacionId(null)
    }
  }

  const cargarPostulacionesGlobales = async (override = null) => {
    try {
      setLoadingPostulacionesGlobales(true)
      setError(null)
      const f = override || filtrosPostulaciones
      const params = {
        ...(f.estado ? { estado: f.estado } : {}),
        ...(f.fecha_desde ? { fecha_desde: f.fecha_desde } : {}),
        ...(f.fecha_hasta ? { fecha_hasta: f.fecha_hasta } : {}),
      }
      const resp = await accesoMascotasService.listarPostulaciones(params)
      if (resp?.success) {
        setPostulacionesGlobales(Array.isArray(resp.data) ? resp.data : [])
      } else {
        setError(resp?.error || 'No se pudieron cargar las postulaciones')
        setPostulacionesGlobales([])
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al cargar postulaciones')
      setPostulacionesGlobales([])
    } finally {
      setLoadingPostulacionesGlobales(false)
    }
  }

  const fetchDuenos = async () => {
    try {
      setLoadingDuenos(true)
      const response = await usuarioService.getAll({ tipo_usuario: 'DUEÑO', per_page: 100 })
      
      if (response.success) {
        if (response.data.data) {
          setDuenos(response.data.data)
        } else {
          setDuenos(Array.isArray(response.data) ? response.data : [])
        }
      }
    } catch (err) {
      console.error('Error fetching dueños:', err)
    } finally {
      setLoadingDuenos(false)
    }
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      if (formData.origen === 'Comprado' && !String(formData.criadero || '').trim()) {
        setError('Si la mascota fue comprada, indica el criadero, tienda o lugar de compra.')
        setSubmitting(false)
        return
      }

      const { origen, criadero, fecha_adopcion, ...formRest } = formData
      const mascotaData = {
        ...formRest,
        peso: formData.peso ? parseFloat(formData.peso) : null,
        altura: formData.altura ? parseFloat(formData.altura) : null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        id_dueño: formData.id_dueño || user.idUsuario
      }
      if (!mascotaData.tipo_raza) delete mascotaData.tipo_raza

      if (origen === 'Adoptado' || origen === 'Comprado') {
        mascotaData.origen = origen
        mascotaData.criadero = origen === 'Comprado' ? String(criadero || '').trim() : null
        if (fecha_adopcion) mascotaData.fecha_adopcion = fecha_adopcion
      }

      const response = await mascotaService.create(mascotaData)
      
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        fetchMascotas()
      } else {
        setError(response.error || 'Error al crear mascota')
      }
    } catch (err) {
      setError(err.message || 'Error al crear mascota')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeactivate = async (mascota) => {
    const mascotaId = mascota?.id_mascota || mascota?.idMascota
    if (!mascotaId) return

    const nombre = mascota?.nombre ? ` "${mascota.nombre}"` : ''
    if (!confirm(`La mascota${nombre} se moverá a tus recuerdos y no aparecerá en los listados de mascotas vivas. ¿Deseas continuar?`)) {
      return
    }

    try {
      setError(null)
      const response = await mascotaService.deactivate(mascotaId)
      if (response.success) {
        fetchMascotas()
      } else {
        setError(response.error || 'Error al desactivar la mascota')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al desactivar la mascota')
    }
  }

  const handleActivate = async (mascota) => {
    const mascotaId = mascota?.id_mascota || mascota?.idMascota
    if (!mascotaId) return

    const nombre = mascota?.nombre ? ` "${mascota.nombre}"` : ''
    if (!confirm(`La mascota${nombre} volverá a mostrarse como viva en tu listado. ¿Deseas continuar?`)) {
      return
    }

    try {
      setError(null)
      const response = await mascotaService.activate(mascotaId)
      if (response.success) {
        fetchMascotas()
      } else {
        setError(response.error || 'Error al reactivar la mascota')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al reactivar la mascota')
    }
  }

  const openFallecidaModal = (mascota) => {
    const today = new Date().toISOString().split('T')[0]
    setMascotaAccion(mascota)
    setFallecidaFormData({
      fecha_fallecimiento: today,
      motivo_fallecimiento: ''
    })
    setProcessingAction(false)
    setShowFallecidaModal(true)
  }

  const handleConfirmFallecida = async (e) => {
    e.preventDefault()
    if (!mascotaAccion) return

    const mascotaId = mascotaAccion.id_mascota || mascotaAccion.idMascota
    if (!mascotaId) return

    try {
      setProcessingAction(true)
      setError(null)

      const payload = {
        ...(fallecidaFormData.fecha_fallecimiento && { fecha_fallecimiento: fallecidaFormData.fecha_fallecimiento }),
        ...(fallecidaFormData.motivo_fallecimiento && { motivo_fallecimiento: fallecidaFormData.motivo_fallecimiento })
      }

      const response = await mascotaService.markAsFallecida(mascotaId, payload)
      if (response.success) {
        setShowFallecidaModal(false)
        setMascotaAccion(null)
        setFallecidaFormData({ fecha_fallecimiento: '', motivo_fallecimiento: '' })
        fetchMascotas()
      } else {
        setError(response.error || 'Error al marcar la mascota como fallecida')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al marcar la mascota como fallecida')
    } finally {
      setProcessingAction(false)
    }
  }

  const handleDelete = async (mascota) => {
    const mascotaId = mascota?.id_mascota || mascota?.idMascota
    if (!mascotaId) return

    const nombre = mascota?.nombre ? ` "${mascota.nombre}"` : ''
    if (!confirm(`Esta acción eliminará permanentemente la mascota${nombre} y todo su historial asociado. ¿Deseas continuar?`)) {
      return
    }

    try {
      setError(null)
      const response = await mascotaService.delete(mascotaId)
      if (response.success) {
        fetchMascotas()
      } else {
        setError(response.error || 'Error al eliminar la mascota')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al eliminar la mascota')
    }
  }

  const resetForm = () => {
    setFormData({
      nombre: '',
      especie: '',
      raza: '',
      tipo_raza: '',
      fecha_nacimiento: '',
      sexo: '',
      color: '',
      peso: '',
      altura: '',
      microchip: '',
      observaciones: '',
      origen: '',
      criadero: '',
      fecha_adopcion: '',
      id_dueño: isDueno ? user?.idUsuario : ''
    })
  }

  const clearFilters = () => {
    setFiltros({ search: '', especie: '', estado: '', tipo_raza: '' })
  }

  // Si el usuario puede gestionar esta mascota (dueño de la mascota o admin/superadmin)
  const canManageThisMascota = (m) => {
    const idDueño = m?.id_dueño ?? m?.idDueño
    const myId = user?.idUsuario ?? user?.id_usuario
    return (isDueno && idDueño == myId) || isAdmin
  }

  // Funciones para condiciones de salud
  const openCondicionesModal = async (mascota) => {
    setSelectedMascota(mascota)
    setShowCondicionesModal(true)
    await fetchCondicionesMascota(mascota.id_mascota || mascota.idMascota)
    await fetchCatalogoCondiciones()
    await fetchVeterinarios()
  }

  const fetchCondicionesMascota = async (idMascota) => {
    try {
      setLoadingCondiciones(true)
      const response = await mascotaCondicionService.getByMascota(idMascota)
      if (response.success) {
        setCondicionesMascota(response.data || [])
      }
    } catch (err) {
      console.error('Error fetching condiciones:', err)
      setError(err.message || 'Error al cargar condiciones')
    } finally {
      setLoadingCondiciones(false)
    }
  }

  const fetchCatalogoCondiciones = async () => {
    try {
      setLoadingCatalogo(true)
      const response = await condicionSaludService.getAll({ activa: true, per_page: 100 })
      if (response.success) {
        setCatalogoCondiciones(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching catálogo:', err)
    } finally {
      setLoadingCatalogo(false)
    }
  }

  const fetchVeterinarios = async () => {
    try {
      const response = await veterinarioService.getAll({ activo: true, per_page: 100 })
      if (response.success) {
        setVeterinarios(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching veterinarios:', err)
    }
  }

  const handleAddCondicion = async (e) => {
    e.preventDefault()
    setSubmittingCondicion(true)
    try {
      const idMascota = selectedMascota.id_mascota || selectedMascota.idMascota
      const response = await mascotaCondicionService.create(idMascota, condicionFormData)
      if (response.success) {
        setShowAddCondicionModal(false)
        resetCondicionForm()
        await fetchCondicionesMascota(idMascota)
      } else {
        setError(response.error || 'Error al agregar condición')
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.details ||
        err.message ||
        'Error al agregar condición'
      )
    } finally {
      setSubmittingCondicion(false)
    }
  }

  const handleUpdateCondicion = async (e) => {
    e.preventDefault()
    setSubmittingCondicion(true)
    try {
      const idMascota = selectedMascota.id_mascota || selectedMascota.idMascota
      const idCondicion = editingCondicion.id_mascota_condicion || editingCondicion.idMascotaCondicion
      const response = await mascotaCondicionService.update(idMascota, idCondicion, condicionFormData)
      if (response.success) {
        setShowAddCondicionModal(false)
        setEditingCondicion(null)
        resetCondicionForm()
        await fetchCondicionesMascota(idMascota)
      } else {
        setError(response.error || 'Error al actualizar condición')
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.details ||
        err.message ||
        'Error al actualizar condición'
      )
    } finally {
      setSubmittingCondicion(false)
    }
  }

  const handleDeleteCondicion = async (idCondicion) => {
    if (!confirm('¿Estás seguro de que quieres desactivar esta condición de salud?')) {
      return
    }

    try {
      const idMascota = selectedMascota.id_mascota || selectedMascota.idMascota
      const response = await mascotaCondicionService.delete(idMascota, idCondicion)
      if (response.success) {
        await fetchCondicionesMascota(idMascota)
      } else {
        setError(response.error || 'Error al eliminar condición')
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.response?.data?.details ||
        err.message ||
        'Error al eliminar condición'
      )
    }
  }

  const openEditCondicion = (condicion) => {
    setEditingCondicion(condicion)
    setCondicionFormData({
      id_condicion_salud:
        condicion.id_condicion_salud ||
        condicion.id_condicion ||
        condicion.idCondicionSalud ||
        condicion.condicionSalud?.id_condicion ||
        condicion.condicion_salud?.id_condicion ||
        '',
      fecha_diagnostico: condicion.fecha_diagnostico ? condicion.fecha_diagnostico.split('T')[0] : new Date().toISOString().split('T')[0],
      id_veterinario: condicion.id_veterinario || '',
      id_ficha_clinica: condicion.id_ficha_clinica || '',
      severidad: condicion.severidad || 'MODERADA',
      estado: condicion.estado || 'EN_TRATAMIENTO',
      notas: condicion.notas || ''
    })
    setShowAddCondicionModal(true)
  }

  const resetCondicionForm = () => {
    setCondicionFormData({
      id_condicion_salud: '',
      fecha_diagnostico: new Date().toISOString().split('T')[0],
      id_veterinario: '',
      id_ficha_clinica: '',
      severidad: 'MODERADA',
      estado: 'EN_TRATAMIENTO',
      notas: ''
    })
    setEditingCondicion(null)
  }

  const getSeveridadBadge = (severidad) => {
    const variants = {
      'LEVE': 'success',
      'MODERADA': 'warning',
      'GRAVE': 'danger'
    }
    return <Badge variant={variants[severidad] || 'default'}>{severidad}</Badge>
  }

  const getEstadoBadge = (estado) => {
    const variants = {
      'ACTIVA': 'info',
      'EN_TRATAMIENTO': 'warning',
      'RESUELTA': 'success',
      'REMISION': 'default'
    }
    return <Badge variant={variants[estado] || 'default'}>{estado}</Badge>
  }

  // Funciones para registros de peso
  const openDetailModal = async (mascota) => {
    setSelectedMascota(mascota)
    setShowDetailModal(true)
    setLoadingDetail(true)
    setDetailExtrasLoading(true)
    setError(null)
    
    try {
      const idMascota = mascota.id_mascota || mascota.idMascota

      const [mascotaRes, pesoRes, condicionesRes] = await Promise.all([
        mascotaService.getById(idMascota),
        registroPesoService.getByMascota(idMascota).catch(() => null),
        mascotaCondicionService.getByMascota(idMascota).catch(() => null),
      ])

      if (mascotaRes?.success) setMascotaDetail(mascotaRes.data)
      else setError(mascotaRes?.error || 'Error al cargar detalles de la mascota')

      const pesoList =
        (pesoRes?.success && (pesoRes?.data?.data || pesoRes?.data)) ||
        pesoRes?.data?.data ||
        pesoRes?.data ||
        []
      const condicionesList =
        (condicionesRes?.success && (condicionesRes?.data || [])) ||
        condicionesRes?.data ||
        []

      setDetailRegistrosPeso(Array.isArray(pesoList) ? pesoList : [])
      setDetailCondiciones(Array.isArray(condicionesList) ? condicionesList : [])
    } catch (err) {
      setError(err.message || 'Error al cargar detalles de la mascota')
    } finally {
      setLoadingDetail(false)
      setDetailExtrasLoading(false)
    }
  }

  const openRegistrosPesoModal = async (mascota) => {
    setSelectedMascota(mascota)
    setShowRegistrosPesoModal(true)
    await fetchRegistrosPeso(mascota.id_mascota || mascota.idMascota)
  }

  const fetchRegistrosPeso = async (idMascota) => {
    try {
      setLoadingRegistrosPeso(true)
      const response = await registroPesoService.getByMascota(idMascota)
      if (response.success) {
        setRegistrosPeso(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching registros peso:', err)
      setError(err.message || 'Error al cargar registros de peso')
    } finally {
      setLoadingRegistrosPeso(false)
    }
  }

  const openAddRegistroPesoModal = (registro = null) => {
    if (registro) {
      setEditingRegistroPeso(registro)
      setRegistroPesoFormData({
        peso: registro.peso || '',
        fecha_registro: registro.fecha_registro ? new Date(registro.fecha_registro).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        tipo_registro: registro.tipo_registro || 'CASA',
        observaciones: registro.observaciones || ''
      })
    } else {
      setEditingRegistroPeso(null)
      setRegistroPesoFormData({
        peso: selectedMascota?.peso || '',
        fecha_registro: new Date().toISOString().split('T')[0],
        tipo_registro: 'CASA',
        observaciones: ''
      })
    }
    setShowAddRegistroPesoModal(true)
  }

  const handleSaveRegistroPeso = async (e) => {
    e.preventDefault()
    try {
      setSubmittingRegistroPeso(true)
      setError(null)

      const data = {
        id_mascota: selectedMascota.id_mascota || selectedMascota.idMascota,
        peso: parseFloat(registroPesoFormData.peso),
        fecha_registro: registroPesoFormData.fecha_registro,
        tipo_registro: registroPesoFormData.tipo_registro,
        ...(registroPesoFormData.observaciones && { observaciones: registroPesoFormData.observaciones })
      }

      if (editingRegistroPeso) {
        const response = await registroPesoService.update(
          editingRegistroPeso.id_registro || editingRegistroPeso.idRegistro,
          data
        )
        if (response.success) {
          setShowAddRegistroPesoModal(false)
          resetRegistroPesoForm()
          await fetchRegistrosPeso(selectedMascota.id_mascota || selectedMascota.idMascota)
        } else {
          setError(response.error || 'Error al actualizar registro de peso')
        }
      } else {
        const response = await registroPesoService.create(data)
        if (response.success) {
          setShowAddRegistroPesoModal(false)
          resetRegistroPesoForm()
          await fetchRegistrosPeso(selectedMascota.id_mascota || selectedMascota.idMascota)
        } else {
          setError(response.error || 'Error al crear registro de peso')
        }
      }
    } catch (err) {
      setError(err.message || 'Error al guardar registro de peso')
      console.error('Error saving registro peso:', err)
    } finally {
      setSubmittingRegistroPeso(false)
    }
  }

  const handleDeleteRegistroPeso = async (registro) => {
    if (!window.confirm(`¿Estás seguro de eliminar el registro de peso del ${new Date(registro.fecha_registro).toLocaleDateString()}?`)) {
      return
    }

    try {
      setError(null)
      const response = await registroPesoService.delete(registro.id_registro || registro.idRegistro)
      if (response.success) {
        await fetchRegistrosPeso(selectedMascota.id_mascota || selectedMascota.idMascota)
      } else {
        setError(response.error || 'Error al eliminar registro de peso')
      }
    } catch (err) {
      setError(err.message || 'Error al eliminar registro de peso')
      console.error('Error deleting registro peso:', err)
    }
  }

  const resetRegistroPesoForm = () => {
    setRegistroPesoFormData({
      peso: '',
      fecha_registro: new Date().toISOString().split('T')[0],
      tipo_registro: 'CASA',
      observaciones: ''
    })
    setEditingRegistroPeso(null)
  }

  const hasActiveFilters = filtros.search || filtros.especie || filtros.estado || filtros.tipo_raza

  if (loading && mascotas.length === 0) {
    return <LoadingState message="Cargando mascotas..." size="lg" />
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold">Pet Records</p>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pastel-ink via-primary-600 to-pastel-ink bg-clip-text text-transparent">
            {isDueno ? 'Mis Mascotas' : 'Mascotas'}
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-gray-500">
            {isDueno 
              ? 'Gestiona todas tus mascotas'
              : 'Gestión completa de mascotas del sistema'}
          </p>
        </div>
        <div className="flex w-full sm:w-auto gap-2">
          {isSuperAdmin ? (
            <Button
              variant="secondary"
              onClick={() => {
                setShowPostulacionesGlobalModal(true)
                cargarPostulacionesGlobales()
              }}
              className="inline-flex items-center w-full sm:w-auto justify-center"
            >
              <User className="w-4 h-4 mr-2" />
              Postulaciones
            </Button>
          ) : null}
          {canCreateMascota && (
            <Button onClick={() => setShowCreateModal(true)} className="inline-flex items-center w-full sm:w-auto justify-center">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Mascota
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filtros */}
      <Disclosure 
        title="Filtros de Búsqueda"
        defaultOpen={hasActiveFilters}
        className="bg-white/95 dark:bg-gray-800 rounded-3xl border border-pastel-pink dark:border-gray-700 shadow-soft"
      >
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Input
              label="Búsqueda"
              leftIcon={Search}
              placeholder="Nombre, especie, raza..."
              value={filtros.search}
              onChange={(e) => setFiltros({ ...filtros, search: e.target.value })}
            />
            <Listbox
              label="Especie"
              options={[
                { value: '', label: 'Todas' },
                { value: 'Perro', label: 'Perro' },
                { value: 'Gato', label: 'Gato' },
                { value: 'Conejo', label: 'Conejo' },
                { value: 'Ave', label: 'Ave' },
                { value: 'Hámster', label: 'Hámster' }
              ]}
              value={filtros.especie}
              onChange={(value) => setFiltros({ ...filtros, especie: value })}
            />
            <Listbox
              label="Tipo de raza"
              options={[
                { value: '', label: 'Todos' },
                { value: 'pura', label: 'Pura' },
                { value: 'cruza', label: 'Cruza' },
                { value: 'mixto', label: 'Mixto' }
              ]}
              value={filtros.tipo_raza}
              onChange={(value) => setFiltros({ ...filtros, tipo_raza: value })}
            />
            <Listbox
              label="Estado vital"
              options={[
                { value: '', label: 'Todos' },
                { value: 'ACTIVA', label: 'Viva' },
                { value: 'INACTIVA', label: 'En recuerdos' },
                { value: 'FALLECIDA', label: 'Fallecida' }
              ]}
              value={filtros.estado}
              onChange={(value) => setFiltros({ ...filtros, estado: value })}
            />
          </div>
          <div className="flex justify-end space-x-2">
            {hasActiveFilters && (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpiar Filtros
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={fetchMascotas}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualizar
            </Button>
          </div>
        </div>
      </Disclosure>

      {/* Tabla */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead className="hidden sm:table-cell">Especie</TableHead>
                <TableHead className="hidden md:table-cell">Raza</TableHead>
                <TableHead className="hidden lg:table-cell">Tipo</TableHead>
                <TableHead className="hidden lg:table-cell">Dueño</TableHead>
                <TableHead className="hidden md:table-cell">Peso</TableHead>
                <TableHead>Vital</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mascotas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="8">
                    <EmptyState
                      icon={Heart}
                      title="No hay mascotas registradas"
                      description="Comienza agregando una nueva mascota al sistema"
                      action={
                        <Button onClick={() => setShowCreateModal(true)}>
                          <Plus className="w-4 h-4 mr-2" />
                          Agregar Mascota
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                mascotas.map((mascota) => (
                  <TableRow key={mascota.id_mascota || mascota.idMascota}>
                    <TableCell className="font-medium">
                      <div className="flex items-center space-x-2">
                        <Heart className="w-4 h-4 text-pastel-teal dark:text-primary-400" />
                        <span className="text-gray-900 dark:text-white">{mascota.nombre}</span>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{mascota.especie}</TableCell>
                    <TableCell className="hidden md:table-cell">{mascota.raza || '-'}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {labelTipoRaza(mascota.tipo_raza || mascota.tipoRaza) || '—'}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {mascota.dueno ? (
                        <div className="flex items-center space-x-3">
                          <Avatar user={mascota.dueno} size="sm" />
                          <span>{`${mascota.dueno.nombre} ${mascota.dueno.apellido || ''}`.trim()}</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {mascota.peso ? (
                        <div className="flex items-center space-x-1">
                          <Scale className="w-4 h-4 text-gray-500" />
                          <span>{mascota.peso} kg</span>
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {mascota.estado === 'ACTIVA' && (
                        <Badge variant="success">{labelEstadoMascota(mascota.estado)}</Badge>
                      )}
                      {mascota.estado === 'INACTIVA' && (
                        <Badge variant="danger">{labelEstadoMascota(mascota.estado)}</Badge>
                      )}
                      {mascota.estado === 'FALLECIDA' && (
                        <Badge variant="default">{labelEstadoMascota(mascota.estado)}</Badge>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <ActionsMenu
                        items={[
                          {
                            label: 'Ver detalles',
                            icon: 'view',
                            onClick: () => openDetailModal(mascota)
                          },
                          ...(isDueno && canManageThisMascota(mascota) ? [{
                            label: 'Compartir por QR/Código',
                            icon: QrCode,
                            onClick: () => openShareModal(mascota)
                          }] : []),
                          ...((isDueno || isSuperAdmin) && canManageThisMascota(mascota) ? [{
                            label: 'Postulaciones de cuidadores',
                            icon: User,
                            onClick: () => openPostulacionesModal(mascota)
                          }] : []),
                          {
                            label: 'Condiciones de salud',
                            icon: 'activity',
                            onClick: () => openCondicionesModal(mascota)
                          },
                          {
                            label: 'Registros de peso',
                            icon: 'scale',
                            onClick: () => openRegistrosPesoModal(mascota)
                          },
                          // Ocultar editar para veterinarios
                          ...(!isVeterinario ? [{
                            label: 'Editar',
                            icon: 'edit',
                            onClick: () => navigate(`/mascotas/${mascota.id_mascota || mascota.idMascota}/edit`)
                          }] : []),
                          ...(canManageThisMascota(mascota) && mascota.estado === 'ACTIVA' ? [{
                            label: 'Guardar en recuerdos',
                            icon: Archive,
                            onClick: () => handleDeactivate(mascota)
                          }] : []),
                          ...(canManageThisMascota(mascota) && mascota.estado === 'INACTIVA' ? [{
                            label: 'Reactivar',
                            icon: RotateCcw,
                            onClick: () => handleActivate(mascota)
                          }] : []),
                          ...(canManageThisMascota(mascota) && mascota.estado !== 'FALLECIDA' ? [{
                            label: 'Marcar como fallecida',
                            icon: Skull,
                            onClick: () => openFallecidaModal(mascota)
                          }] : []),
                          ...(canManageThisMascota(mascota) && mascota.estado !== 'FALLECIDA' 
                            ? [{ divider: true }] 
                            : []),
                          ...(canManageThisMascota(mascota) ? [{
                            label: 'Eliminar definitivamente',
                            icon: 'delete',
                            danger: true,
                            onClick: () => handleDelete(mascota)
                          }] : [])
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Paginación */}
      {pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-2xl border border-pastel-pink dark:border-gray-700 p-4 shadow-soft">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
            <span className="font-medium">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> de{' '}
            <span className="font-medium">{pagination.total}</span> resultados
          </div>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
            >
              Anterior
            </Button>
            <div className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
              Página {pagination.current_page} de {pagination.last_page}
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal: Compartir acceso temporal */}
      <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} size="lg">
        <ModalHeader onClose={() => setShowShareModal(false)}>
          Compartir acceso temporal
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            {shareError && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 mt-0.5" />
                <p className="text-red-600 dark:text-red-400 font-medium">{shareError}</p>
              </div>
            )}

            <div className="text-sm text-gray-700 dark:text-gray-300">
              Mascota: <span className="font-semibold">{shareMascota?.nombre || 'N/A'}</span>
            </div>

            {!shareResult ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Listbox
                  label="Alcance"
                  options={[
                    { value: 'HISTORIAL_COMPLETO', label: 'Historial completo' },
                    { value: 'BASICO', label: 'Solo datos básicos' },
                  ]}
                  value={shareForm.alcance}
                  onChange={(value) => setShareForm((p) => ({ ...p, alcance: value }))}
                />
                <Input
                  label="Duración (minutos)"
                  type="number"
                  min={15}
                  max={1440}
                  value={shareForm.duracion_minutos}
                  onChange={(e) => setShareForm((p) => ({ ...p, duracion_minutos: e.target.value }))}
                  leftIcon={Clock}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Código</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-white">{shareResult.codigo_humano}</div>
                    </div>
                    <Button variant="secondary" onClick={() => copyToClipboard(shareResult.codigo_humano)}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar
                    </Button>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 break-all">
                    Token: {shareResult.token}
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 break-all">
                    Enlace: {getShareUrl()}
                  </div>
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Expira: {shareResult.fecha_expiracion ? new Date(shareResult.fecha_expiracion).toLocaleString('es-ES') : 'N/A'}
                  </div>
                </div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex flex-col items-center gap-2">
                  <img
                    src={getQrImageUrl()}
                    alt="QR de acceso temporal"
                    className="w-44 h-44 rounded-md border border-gray-200 dark:border-gray-700 bg-white"
                  />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 text-center">
                    El veterinario puede escanear este QR o abrir el enlace.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" onClick={() => copyToClipboard(getShareUrl())}>
                    <Link2 className="w-4 h-4 mr-2" />
                    Copiar enlace
                  </Button>
                  <Button variant="secondary" onClick={handleNativeShare}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Compartir
                  </Button>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Entrega el <span className="font-semibold">código</span>, el <span className="font-semibold">QR</span> o el <span className="font-semibold">enlace</span> al veterinario para validar acceso.
                </div>
              </div>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowShareModal(false)}>
            Cerrar
          </Button>
          {!shareResult ? (
            <Button onClick={handleGenerateShare} loading={sharing}>
              <QrCode className="w-4 h-4 mr-2" />
              Generar código
            </Button>
          ) : (
            <Button variant="secondary" onClick={handleRevokeShare} loading={sharing}>
              Revocar acceso
            </Button>
          )}
        </ModalFooter>
      </Modal>

      {/* Modal: Postulaciones de cuidadores */}
      <Modal
        isOpen={showPostulacionesModal}
        onClose={() => {
          setShowPostulacionesModal(false)
          setPostulacionesMascota([])
          setSelectedMascota(null)
        }}
        size="xl"
      >
        <ModalHeader
          onClose={() => {
            setShowPostulacionesModal(false)
            setPostulacionesMascota([])
            setSelectedMascota(null)
          }}
        >
          Postulaciones de cuidadores - {selectedMascota?.nombre || 'Mascota'}
        </ModalHeader>
        <ModalContent>
          {loadingPostulaciones ? (
            <LoadingState message="Cargando postulaciones..." />
          ) : postulacionesMascota.length === 0 ? (
            <EmptyState
              icon={User}
              title="Sin postulaciones"
              description="Aun no hay candidatos para esta mascota."
            />
          ) : (
            <div className="space-y-3">
              {postulacionesMascota.map((p) => {
                const ps = p.pet_sitter || {}
                const nombre = `${ps.nombre || ''} ${ps.apellido || ''}`.trim() || 'Sin nombre'
                return (
                  <div
                    key={p.id_postulacion}
                    className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900/20"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-base font-semibold text-gray-900 dark:text-white">{nombre}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Postulado: {p.fecha_creacion ? new Date(p.fecha_creacion).toLocaleString('es-ES') : 'N/A'}
                        </div>
                      </div>
                      <Badge
                        variant={p.estado === 'APROBADA' ? 'success' : p.estado === 'RECHAZADA' ? 'danger' : 'warning'}
                      >
                        {p.estado}
                      </Badge>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-gray-300">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span>{ps.email || 'Sin email'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span>{ps.telefono || 'Sin telefono'}</span>
                      </div>
                      <div>Empresa: <span className="font-medium">{ps.empresa || '-'}</span></div>
                      <div>Experiencia: <span className="font-medium">{ps.experiencia_anos ?? '-'} anos</span></div>
                      <div>Perfil: <span className="font-medium">{ps.estilo_perfil || '-'}</span></div>
                      <div>
                        Calificacion: <span className="font-medium">{ps.calificacion_promedio ?? '-'} ({ps.total_servicios ?? 0} servicios)</span>
                      </div>
                    </div>

                    {p.mensaje ? (
                      <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-300">
                        {p.mensaje}
                      </div>
                    ) : null}

                    {(isDueno || isSuperAdmin) && p.estado === 'PENDIENTE' ? (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button
                          onClick={() => resolverPostulacion(p, 'aprobar')}
                          loading={resolvingPostulacionId === p.id_postulacion}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Aprobar postulante
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => resolverPostulacion(p, 'rechazar')}
                          loading={resolvingPostulacionId === p.id_postulacion}
                        >
                          Rechazar
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPostulacionesModal(false)
              setPostulacionesMascota([])
              setSelectedMascota(null)
            }}
          >
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal: Bandeja global de postulaciones (SUPERADMIN) */}
      <Modal
        isOpen={showPostulacionesGlobalModal}
        onClose={() => {
          setShowPostulacionesGlobalModal(false)
          setPostulacionesGlobales([])
        }}
        size="xl"
      >
        <ModalHeader
          onClose={() => {
            setShowPostulacionesGlobalModal(false)
            setPostulacionesGlobales([])
          }}
        >
          Bandeja global de postulaciones
        </ModalHeader>
        <ModalContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
            <Listbox
              label="Estado"
              options={[
                { value: '', label: 'Todos' },
                { value: 'PENDIENTE', label: 'Pendiente' },
                { value: 'APROBADA', label: 'Aprobada' },
                { value: 'RECHAZADA', label: 'Rechazada' },
              ]}
              value={filtrosPostulaciones.estado}
              onChange={(value) => setFiltrosPostulaciones((p) => ({ ...p, estado: value }))}
            />
            <Input
              type="date"
              label="Desde"
              value={filtrosPostulaciones.fecha_desde}
              onChange={(e) => setFiltrosPostulaciones((p) => ({ ...p, fecha_desde: e.target.value }))}
            />
            <Input
              type="date"
              label="Hasta"
              value={filtrosPostulaciones.fecha_hasta}
              onChange={(e) => setFiltrosPostulaciones((p) => ({ ...p, fecha_hasta: e.target.value }))}
            />
            <div className="flex items-end gap-2">
              <Button
                className="w-full"
                onClick={() => cargarPostulacionesGlobales()}
                loading={loadingPostulacionesGlobales}
              >
                Aplicar
              </Button>
            </div>
          </div>

          {loadingPostulacionesGlobales ? (
            <LoadingState message="Cargando postulaciones..." />
          ) : postulacionesGlobales.length === 0 ? (
            <EmptyState
              icon={User}
              title="Sin resultados"
              description="No hay postulaciones para los filtros seleccionados."
            />
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
              {postulacionesGlobales.map((p) => {
                const ps = p.pet_sitter || {}
                const nombre = `${ps.nombre || ''} ${ps.apellido || ''}`.trim() || 'Sin nombre'
                return (
                  <div key={p.id_postulacion} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <div className="font-semibold text-gray-900 dark:text-white">{nombre}</div>
                        <div>Mascota: <span className="font-medium">{p.mascota?.nombre || 'N/A'}</span></div>
                        <div>Fecha: {p.fecha_creacion ? new Date(p.fecha_creacion).toLocaleString('es-ES') : 'N/A'}</div>
                      </div>
                      <Badge variant={p.estado === 'APROBADA' ? 'success' : p.estado === 'RECHAZADA' ? 'danger' : 'warning'}>
                        {p.estado}
                      </Badge>
                    </div>
                    <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 flex flex-wrap gap-4">
                      <span className="inline-flex items-center gap-1"><Mail className="w-4 h-4" /> {ps.email || 'Sin email'}</span>
                      <span className="inline-flex items-center gap-1"><Phone className="w-4 h-4" /> {ps.telefono || 'Sin telefono'}</span>
                    </div>
                    {p.estado === 'PENDIENTE' ? (
                      <div className="mt-3 flex gap-2">
                        <Button
                          onClick={async () => {
                            await resolverPostulacion(p, 'aprobar')
                            await cargarPostulacionesGlobales()
                          }}
                          loading={resolvingPostulacionId === p.id_postulacion}
                        >
                          Aprobar
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={async () => {
                            await resolverPostulacion(p, 'rechazar')
                            await cargarPostulacionesGlobales()
                          }}
                          loading={resolvingPostulacionId === p.id_postulacion}
                        >
                          Rechazar
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPostulacionesGlobalModal(false)
              setPostulacionesGlobales([])
            }}
          >
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Crear Mascota */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} size="lg">
        <ModalHeader onClose={() => { setShowCreateModal(false); resetForm(); }}>
          Nueva Mascota
        </ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Input
                  label="Nombre"
                  required
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </div>
              <Listbox
                label="Especie"
                options={[
                  { value: '', label: 'Seleccione...' },
                  { value: 'Perro', label: 'Perro' },
                  { value: 'Gato', label: 'Gato' },
                  { value: 'Conejo', label: 'Conejo' },
                  { value: 'Ave', label: 'Ave' },
                  { value: 'Hámster', label: 'Hámster' }
                ]}
                value={formData.especie}
                onChange={(value) => setFormData({ ...formData, especie: value })}
                placeholder="Seleccione especie..."
              />
              <Input
                label="Raza"
                value={formData.raza}
                onChange={(e) => setFormData({ ...formData, raza: e.target.value })}
              />
              <Listbox
                label="Tipo de raza"
                options={[
                  { value: '', label: 'No indicado' },
                  { value: 'pura', label: 'Pura' },
                  { value: 'cruza', label: 'Cruza' },
                  { value: 'mixto', label: 'Mixto' }
                ]}
                value={formData.tipo_raza}
                onChange={(value) => setFormData({ ...formData, tipo_raza: value })}
                placeholder="Opcional..."
              />
              {isAdmin && (
                <div className="col-span-2">
                  <Listbox
                    label="Dueño"
                    options={[
                      { value: '', label: 'Seleccione un dueño...' },
                      ...duenos.map((dueno) => ({
                        value: dueno.id_usuario || dueno.idUsuario,
                        label: `${dueno.nombre} ${dueno.apellido || ''} (${dueno.email})`
                      }))
                    ]}
                    value={formData.id_dueño}
                    onChange={(value) => setFormData({ ...formData, id_dueño: value })}
                    disabled={loadingDuenos}
                    placeholder="Seleccione dueño..."
                  />
                </div>
              )}
              <Input
                label="Fecha de Nacimiento"
                type="date"
                value={formData.fecha_nacimiento}
                onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
              />
              <Listbox
                label="Sexo"
                options={[
                  { value: '', label: 'Seleccione...' },
                  { value: 'M', label: 'Macho' },
                  { value: 'H', label: 'Hembra' }
                ]}
                value={formData.sexo}
                onChange={(value) => setFormData({ ...formData, sexo: value })}
                placeholder="Seleccione sexo..."
              />
              <Input
                label="Color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
              <Input
                label="Peso (kg)"
                type="number"
                step="0.1"
                value={formData.peso}
                onChange={(e) => setFormData({ ...formData, peso: e.target.value })}
              />
              <Input
                label="Altura (cm)"
                type="number"
                step="0.1"
                value={formData.altura}
                onChange={(e) => setFormData({ ...formData, altura: e.target.value })}
              />
              <div className="col-span-2">
                <Input
                  label="Microchip"
                  value={formData.microchip}
                  onChange={(e) => setFormData({ ...formData, microchip: e.target.value })}
                />
              </div>

              <div className="col-span-2 rounded-xl border border-pastel-pink/60 dark:border-gray-600 bg-pastel-cream/30 dark:bg-gray-800/40 p-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Procedencia</h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    ¿Adoptado o comprado? Si fue compra, indicá criadero o tienda (no hace falta repetirlo en observaciones).
                  </p>
                </div>
                <Listbox
                  label="Origen"
                  options={[
                    { value: '', label: 'No indicado' },
                    { value: 'Adoptado', label: 'Adoptado' },
                    { value: 'Comprado', label: 'Comprado' }
                  ]}
                  value={formData.origen}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      origen: value,
                      criadero: value === 'Comprado' ? formData.criadero : ''
                    })
                  }
                />
                {formData.origen === 'Comprado' ? (
                  <Input
                    label="Criadero, tienda o vendedor"
                    required
                    placeholder="Ej.: Criadero Los Robles, PetShop X…"
                    value={formData.criadero}
                    onChange={(e) => setFormData({ ...formData, criadero: e.target.value })}
                  />
                ) : null}
                {(formData.origen === 'Adoptado' || formData.origen === 'Comprado') ? (
                  <Input
                    label={formData.origen === 'Adoptado' ? 'Fecha de adopción (opcional)' : 'Fecha de compra (opcional)'}
                    type="date"
                    value={formData.fecha_adopcion}
                    onChange={(e) => setFormData({ ...formData, fecha_adopcion: e.target.value })}
                  />
                ) : null}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Observaciones
                </label>
                <textarea
                  value={formData.observaciones}
                  onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                  rows={3}
                  className="input resize-none"
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Crear Mascota
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Condiciones de Salud */}
      <Modal 
        isOpen={showCondicionesModal} 
        onClose={() => { 
          setShowCondicionesModal(false)
          setSelectedMascota(null)
          setCondicionesMascota([])
          resetCondicionForm()
        }} 
        size="xl"
      >
        <ModalHeader onClose={() => { 
          setShowCondicionesModal(false)
          setSelectedMascota(null)
          setCondicionesMascota([])
          resetCondicionForm()
        }}>
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <span>Condiciones de Salud - {selectedMascota?.nombre}</span>
          </div>
        </ModalHeader>
        <ModalContent>
          {loadingCondiciones ? (
            <LoadingState message="Cargando condiciones..." />
          ) : (
            <div className="space-y-4">
              {(isVeterinario || isAdmin) && (
                <div className="flex justify-end">
                  <Button 
                    onClick={() => {
                      resetCondicionForm()
                      setShowAddCondicionModal(true)
                    }}
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Condición
                  </Button>
                </div>
              )}

              {condicionesMascota.length === 0 ? (
                <EmptyState
                  icon={Activity}
                  title="No hay condiciones de salud registradas"
                  description="Esta mascota no tiene condiciones de salud registradas."
                />
              ) : (
                <div className="space-y-3">
                  {condicionesMascota.map((condicion) => (
                    <Card key={condicion.id_mascota_condicion || condicion.idMascotaCondicion} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-gray-900 dark:text-white">
                              {condicion.condicionSalud?.nombre || 'Condición no especificada'}
                            </h4>
                            {getSeveridadBadge(condicion.severidad)}
                            {getEstadoBadge(condicion.estado)}
                          </div>
                          {condicion.condicionSalud?.descripcion && (
                            <p className="text-sm text-gray-700 dark:text-gray-500 mb-2">
                              {condicion.condicionSalud.descripcion}
                            </p>
                          )}
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-gray-500">
                            <div>
                              <span className="font-medium">Fecha diagnóstico:</span>{' '}
                              {condicion.fecha_diagnostico ? new Date(condicion.fecha_diagnostico).toLocaleDateString('es-ES') : '-'}
                            </div>
                            {condicion.fecha_resolucion && (
                              <div>
                                <span className="font-medium">Fecha resolución:</span>{' '}
                                {new Date(condicion.fecha_resolucion).toLocaleDateString('es-ES')}
                              </div>
                            )}
                            {condicion.veterinario?.usuario && (
                              <div>
                                <span className="font-medium">Veterinario:</span>{' '}
                                {condicion.veterinario.usuario.nombre} {condicion.veterinario.usuario.apellido || ''}
                              </div>
                            )}
                            {condicion.condicionSalud?.categoria && (
                              <div>
                                <span className="font-medium">Categoría:</span> {condicion.condicionSalud.categoria}
                              </div>
                            )}
                          </div>
                          {condicion.notas && (
                            <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded text-sm text-gray-700 dark:text-gray-300">
                              <span className="font-medium">Notas:</span> {condicion.notas}
                            </div>
                          )}
                        </div>
                        {(isVeterinario || isAdmin) && (
                          <div className="flex space-x-2 ml-4">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditCondicion(condicion)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCondicion(condicion.id_mascota_condicion || condicion.idMascotaCondicion)}
                            >
                              <Trash2 className="w-4 h-4 text-red-700" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Modal Agregar/Editar Condición */}
      <Modal 
        isOpen={showAddCondicionModal} 
        onClose={() => { 
          setShowAddCondicionModal(false)
          resetCondicionForm()
        }} 
        size="lg"
      >
        <ModalHeader onClose={() => { 
          setShowAddCondicionModal(false)
          resetCondicionForm()
        }}>
          {editingCondicion ? 'Editar Condición de Salud' : 'Agregar Condición de Salud'}
        </ModalHeader>
        <form onSubmit={editingCondicion ? handleUpdateCondicion : handleAddCondicion}>
          <ModalContent>
            <div className="space-y-4">
              <Listbox
                label="Condición de Salud"
                required
                options={[
                  { value: '', label: 'Seleccione una condición...' },
                  ...catalogoCondiciones.map((cond) => ({
                    value: cond.id_condicion || cond.idCondicion,
                    label: `${cond.nombre}${cond.tipo ? ` (${cond.tipo})` : ''}`
                  }))
                ]}
                value={condicionFormData.id_condicion_salud}
                onChange={(value) => setCondicionFormData({ ...condicionFormData, id_condicion_salud: value })}
                disabled={loadingCatalogo}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Fecha de Diagnóstico"
                  type="date"
                  required
                  value={condicionFormData.fecha_diagnostico}
                  onChange={(e) => setCondicionFormData({ ...condicionFormData, fecha_diagnostico: e.target.value })}
                />

                <Listbox
                  label="Severidad"
                  required
                  options={[
                    { value: 'LEVE', label: 'Leve' },
                    { value: 'MODERADA', label: 'Moderada' },
                    { value: 'GRAVE', label: 'Grave' }
                  ]}
                  value={condicionFormData.severidad}
                  onChange={(value) => setCondicionFormData({ ...condicionFormData, severidad: value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Listbox
                  label="Estado de la condición"
                  required
                  options={[
                    { value: 'ACTIVA', label: 'Activa' },
                    { value: 'EN_TRATAMIENTO', label: 'En Tratamiento' },
                    { value: 'RESUELTA', label: 'Resuelta' },
                    { value: 'REMISION', label: 'Remisión' }
                  ]}
                  value={condicionFormData.estado}
                  onChange={(value) => setCondicionFormData({ ...condicionFormData, estado: value })}
                />

                <Listbox
                  label="Veterinario (Opcional)"
                  options={[
                    { value: '', label: 'Seleccione un veterinario...' },
                    ...veterinarios.map((vet) => ({
                      value: vet.id_veterinario || vet.idVeterinario,
                      label: `${vet.usuario?.nombre || ''} ${vet.usuario?.apellido || ''}`.trim() || 'Veterinario'
                    }))
                  ]}
                  value={condicionFormData.id_veterinario}
                  onChange={(value) => setCondicionFormData({ ...condicionFormData, id_veterinario: value })}
                />
              </div>

              <Textarea
                label="Notas (Opcional)"
                rows={3}
                value={condicionFormData.notas}
                onChange={(e) => setCondicionFormData({ ...condicionFormData, notas: e.target.value })}
                placeholder="Notas adicionales sobre la condición..."
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => { 
                setShowAddCondicionModal(false)
                resetCondicionForm()
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={submittingCondicion}>
              {editingCondicion ? 'Actualizar' : 'Agregar'} Condición
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Registros de Peso */}
      <Modal 
        isOpen={showRegistrosPesoModal} 
        onClose={() => { 
          setShowRegistrosPesoModal(false)
          setSelectedMascota(null)
          setRegistrosPeso([])
          resetRegistroPesoForm()
        }} 
        size="lg"
      >
        <ModalHeader onClose={() => { 
          setShowRegistrosPesoModal(false)
          setSelectedMascota(null)
          setRegistrosPeso([])
          resetRegistroPesoForm()
        }}>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Registros de Peso - {selectedMascota?.nombre}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-500 mt-1">
                Historial de peso de {selectedMascota?.nombre}
              </p>
            </div>
            {(isDueno || isVeterinario || isAdmin) && (
              <Button 
                onClick={() => openAddRegistroPesoModal()}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Registro
              </Button>
            )}
          </div>
        </ModalHeader>
        <ModalContent>
          {loadingRegistrosPeso ? (
            <LoadingState message="Cargando registros de peso..." />
          ) : registrosPeso.length === 0 ? (
            <EmptyState
              icon={Scale}
              title="No hay registros de peso"
              description="Aún no se han registrado pesos para esta mascota"
              action={
                (isDueno || isVeterinario || isAdmin) && (
                  <Button onClick={() => openAddRegistroPesoModal()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Primer Registro
                  </Button>
                )
              }
            />
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Peso (kg)</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Registrado por</TableHead>
                      <TableHead>Observaciones</TableHead>
                      {(isDueno || isVeterinario || isAdmin) && (
                        <TableHead className="text-right">Acciones</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {registrosPeso.map((registro) => (
                      <TableRow key={registro.id_registro || registro.idRegistro}>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <span>{new Date(registro.fecha_registro).toLocaleDateString('es-ES', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Scale className="w-4 h-4 text-gray-500" />
                            <span className="font-medium">{registro.peso} kg</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={registro.tipo_registro === 'VETERINARIA' ? 'info' : 'primary'}>
                            {registro.tipo_registro === 'VETERINARIA' ? 'Veterinaria' : 'Casa'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {registro.usuario_registro || registro.usuarioRegistro ? (
                            <div className="flex items-center space-x-2">
                              <User className="w-4 h-4 text-gray-500" />
                              <span>
                                {(registro.usuario_registro || registro.usuarioRegistro)?.nombre || ''} {(registro.usuario_registro || registro.usuarioRegistro)?.apellido || ''}
                              </span>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {registro.observaciones ? (
                            <span className="text-sm text-gray-700 dark:text-gray-500">{registro.observaciones}</span>
                          ) : (
                            <span className="text-sm text-gray-500">-</span>
                          )}
                        </TableCell>
                        {(isDueno || isVeterinario || isAdmin) && (
                          <TableCell align="right">
                            <div className="flex items-center justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openAddRegistroPesoModal(registro)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteRegistroPeso(registro)}
                              >
                                <Trash2 className="w-4 h-4 text-red-700" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>

      {/* Modal Agregar/Editar Registro de Peso */}
      <Modal 
        isOpen={showAddRegistroPesoModal} 
        onClose={() => { 
          setShowAddRegistroPesoModal(false)
          resetRegistroPesoForm()
        }} 
        size="md"
      >
        <ModalHeader onClose={() => { 
          setShowAddRegistroPesoModal(false)
          resetRegistroPesoForm()
        }}>
          {editingRegistroPeso ? 'Editar Registro de Peso' : 'Nuevo Registro de Peso'}
        </ModalHeader>
        <form onSubmit={handleSaveRegistroPeso}>
          <ModalContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Peso (kg) *"
                  type="number"
                  step="0.1"
                  min="0.1"
                  required
                  value={registroPesoFormData.peso}
                  onChange={(e) => setRegistroPesoFormData({ ...registroPesoFormData, peso: e.target.value })}
                  placeholder="Ej: 25.5"
                />

                <Input
                  label="Fecha de Registro *"
                  type="date"
                  required
                  value={registroPesoFormData.fecha_registro}
                  onChange={(e) => setRegistroPesoFormData({ ...registroPesoFormData, fecha_registro: e.target.value })}
                />
              </div>

              <Listbox
                label="Tipo de Registro *"
                required
                options={[
                  { value: 'CASA', label: 'Casa' },
                  { value: 'VETERINARIA', label: 'Veterinaria' }
                ]}
                value={registroPesoFormData.tipo_registro}
                onChange={(value) => setRegistroPesoFormData({ ...registroPesoFormData, tipo_registro: value })}
              />

              <Textarea
                label="Observaciones"
                value={registroPesoFormData.observaciones}
                onChange={(e) => setRegistroPesoFormData({ ...registroPesoFormData, observaciones: e.target.value })}
                placeholder="Observaciones adicionales sobre el registro de peso..."
                rows={3}
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => { 
                setShowAddRegistroPesoModal(false)
                resetRegistroPesoForm()
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={submittingRegistroPeso}>
              {editingRegistroPeso ? 'Actualizar' : 'Crear'} Registro
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Detalle Mascota */}
      <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setMascotaDetail(null); }} size="lg">
        <ModalHeader onClose={() => { setShowDetailModal(false); setMascotaDetail(null); }}>
          <div className="flex items-center space-x-2">
            <Heart className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {mascotaDetail?.nombre || selectedMascota?.nombre || 'Detalles de la Mascota'}
            </h3>
          </div>
        </ModalHeader>
        <ModalContent>
          {loadingDetail ? (
            <LoadingState message="Cargando detalles..." />
          ) : mascotaDetail ? (
            <div className="space-y-6">
              {/* Información Básica */}
              <Card>
                <CardHeader>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Información Básica</h4>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Nombre</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{mascotaDetail.nombre}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Especie</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{mascotaDetail.especie}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Raza</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{mascotaDetail.raza || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Tipo de raza</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {labelTipoRaza(mascotaDetail.tipo_raza || mascotaDetail.tipoRaza) || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Sexo</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{mascotaDetail.sexo || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Fecha de Nacimiento</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {mascotaDetail.fecha_nacimiento 
                          ? new Date(mascotaDetail.fecha_nacimiento).toLocaleDateString('es-ES')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Color</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{mascotaDetail.color || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Peso</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {mascotaDetail.peso ? `${mascotaDetail.peso} kg` : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Altura</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {mascotaDetail.altura ? `${mascotaDetail.altura} cm` : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Microchip</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{mascotaDetail.microchip || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Estado vital</p>
                      <p className="mt-1">
                        {mascotaDetail.estado === 'ACTIVA' && (
                          <Badge variant="success">{labelEstadoMascota(mascotaDetail.estado)}</Badge>
                        )}
                        {mascotaDetail.estado === 'INACTIVA' && (
                          <Badge variant="danger">{labelEstadoMascota(mascotaDetail.estado)}</Badge>
                        )}
                        {mascotaDetail.estado === 'FALLECIDA' && (
                          <Badge variant="default">{labelEstadoMascota(mascotaDetail.estado)}</Badge>
                        )}
                      </p>
                    </div>
                    {mascotaDetail.estado === 'FALLECIDA' && (
                      <>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-500">Fecha de fallecimiento</p>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {mascotaDetail.fecha_fallecimiento
                              ? new Date(mascotaDetail.fecha_fallecimiento).toLocaleDateString('es-ES')
                              : '-'}
                          </p>
                        </div>
                        {mascotaDetail.motivo_fallecimiento && (
                          <div className="md:col-span-2">
                            <p className="text-sm text-gray-600 dark:text-gray-500">Notas de despedida</p>
                            <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white whitespace-pre-wrap">
                              {mascotaDetail.motivo_fallecimiento}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {(formatOrigenMascota(mascotaDetail) ||
                mascotaDetail.fecha_adopcion ||
                mascotaDetail.fechaAdopcion ||
                mascotaDetail.criadero) && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Procedencia</h4>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-500">Origen</p>
                        <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                          {formatOrigenMascota(mascotaDetail) || '—'}
                        </p>
                      </div>
                      {(mascotaDetail.fecha_adopcion || mascotaDetail.fechaAdopcion) && (
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-500">
                            {formatOrigenMascota(mascotaDetail) === 'Comprado'
                              ? 'Fecha de compra'
                              : 'Fecha de adopción'}
                          </p>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {new Date(
                              mascotaDetail.fecha_adopcion || mascotaDetail.fechaAdopcion
                            ).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                      )}
                      {mascotaDetail.criadero && (
                        <div className="md:col-span-2">
                          <p className="text-sm text-gray-600 dark:text-gray-500">
                            Criadero / tienda / vendedor
                          </p>
                          <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                            {mascotaDetail.criadero}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dueño */}
              {mascotaDetail.dueno && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Dueño</h4>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-3">
                      <Avatar user={mascotaDetail.dueno} size="md" />
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {`${mascotaDetail.dueno.nombre} ${mascotaDetail.dueno.apellido || ''}`.trim()}
                        </p>
                        {mascotaDetail.dueno.email && (
                          <p className="text-sm text-gray-600 dark:text-gray-500">{mascotaDetail.dueno.email}</p>
                        )}
                        {mascotaDetail.dueno.telefono && (
                          <p className="text-sm text-gray-600 dark:text-gray-500">{mascotaDetail.dueno.telefono}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Observaciones */}
              {mascotaDetail.observaciones && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Observaciones</h4>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {mascotaDetail.observaciones}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Dashboard / actividad (sin cambiar estética: reutiliza Cards existentes) */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Dashboard</h4>
                    {detailExtrasLoading ? (
                      <span className="text-xs text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Cargando…
                      </span>
                    ) : null}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500 inline-flex items-center gap-1">
                        <FileText className="w-4 h-4" /> Fichas
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {Array.isArray(mascotaDetail.fichas_clinicas || mascotaDetail.fichasClinicas)
                          ? (mascotaDetail.fichas_clinicas || mascotaDetail.fichasClinicas).length
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500 inline-flex items-center gap-1">
                        <Calendar className="w-4 h-4" /> Citas
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {Array.isArray(mascotaDetail.citas) ? mascotaDetail.citas.length : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500 inline-flex items-center gap-1">
                        <Scale className="w-4 h-4" /> Peso
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {mascotaDetail.peso ? `${mascotaDetail.peso} kg` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500 inline-flex items-center gap-1">
                        <Stethoscope className="w-4 h-4" /> Condiciones
                      </p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {detailCondiciones.length}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => openRegistrosPesoModal(mascotaDetail)}
                      className="inline-flex items-center gap-2"
                      type="button"
                    >
                      <Scale className="w-4 h-4" />
                      Ver registros de peso
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => openCondicionesModal(mascotaDetail)}
                      className="inline-flex items-center gap-2"
                      type="button"
                    >
                      <Activity className="w-4 h-4" />
                      Ver condiciones
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/mascotas/${mascotaDetail.id_mascota || mascotaDetail.idMascota}/edit`)}
                      className="inline-flex items-center gap-2"
                      type="button"
                    >
                      <Edit className="w-4 h-4" />
                      Editar mascota
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Últimos registros de peso */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Peso (últimos registros)</h4>
                    <Button
                      variant="secondary"
                      onClick={() => openRegistrosPesoModal(mascotaDetail)}
                      type="button"
                    >
                      Ver todos
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {detailRegistrosPeso.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400">No hay registros de peso para esta mascota.</p>
                  ) : (
                    <div className="space-y-2">
                      {[...detailRegistrosPeso]
                        .sort((a, b) => new Date(b.fecha_registro || 0).getTime() - new Date(a.fecha_registro || 0).getTime())
                        .slice(0, 5)
                        .map((row, idx) => (
                          <div
                            key={row.id_registro_peso || row.id || idx}
                            className="flex items-center justify-between gap-4 border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0 last:pb-0"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {row.peso ?? row.valor ?? '—'} kg
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-500">
                                {row.fecha_registro ? new Date(row.fecha_registro).toLocaleDateString('es-ES') : '—'}
                                {row.tipo_registro ? ` · ${row.tipo_registro}` : ''}
                              </p>
                            </div>
                            {row.observaciones ? (
                              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 max-w-[50%]">
                                {row.observaciones}
                              </p>
                            ) : null}
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Condiciones de salud (resumen) */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Condiciones de salud</h4>
                    <Button
                      variant="secondary"
                      onClick={() => openCondicionesModal(mascotaDetail)}
                      type="button"
                    >
                      Ver detalle
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {detailCondiciones.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-400">No hay condiciones registradas para esta mascota.</p>
                  ) : (
                    <div className="space-y-2">
                      {detailCondiciones.slice(0, 6).map((condicion, idx) => (
                        <div
                          key={condicion.id_condicion || condicion.id || idx}
                          className="flex items-start justify-between gap-3 border-b border-gray-200 dark:border-gray-700 pb-2 last:border-0 last:pb-0"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {condicion?.condicion_salud?.nombre || condicion?.condicionSalud?.nombre || 'Condición'}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-500">
                              {condicion.fecha_diagnostico
                                ? new Date(condicion.fecha_diagnostico).toLocaleDateString('es-ES')
                                : '—'}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {condicion.severidad ? getSeveridadBadge(condicion.severidad) : null}
                            {condicion.estado ? getEstadoBadge(condicion.estado) : null}
                          </div>
                        </div>
                      ))}
                      {detailCondiciones.length > 6 ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          +{detailCondiciones.length - 6} más…
                        </p>
                      ) : null}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-500">No se pudieron cargar los detalles de la mascota</p>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => { setShowDetailModal(false); setMascotaDetail(null); }}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Marcar como Fallecida */}
      <Modal 
        isOpen={showFallecidaModal} 
        onClose={() => { 
          setShowFallecidaModal(false)
          setMascotaAccion(null)
          setFallecidaFormData({ fecha_fallecimiento: '', motivo_fallecimiento: '' })
          setProcessingAction(false)
        }}
        size="md"
      >
        <form onSubmit={handleConfirmFallecida}>
          <ModalHeader onClose={() => { 
            setShowFallecidaModal(false)
            setMascotaAccion(null)
            setFallecidaFormData({ fecha_fallecimiento: '', motivo_fallecimiento: '' })
            setProcessingAction(false)
          }}>
            Marcar mascota como fallecida
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Estás a punto de marcar a <span className="font-semibold text-gray-900 dark:text-white">{mascotaAccion?.nombre}</span> como fallecida. 
                Esta acción conservará su historial, pero la mascota no podrá reactivarse posteriormente.
              </p>
              <Input
                label="Fecha de fallecimiento"
                type="date"
                value={fallecidaFormData.fecha_fallecimiento}
                onChange={(e) => setFallecidaFormData({ ...fallecidaFormData, fecha_fallecimiento: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                required
              />
              <Textarea
                label="Notas o recuerdos (opcional)"
                value={fallecidaFormData.motivo_fallecimiento}
                onChange={(e) => setFallecidaFormData({ ...fallecidaFormData, motivo_fallecimiento: e.target.value })}
                rows={4}
                placeholder="Añade notas para recordar a tu mascota..."
              />
            </div>
          </ModalContent>
          <ModalFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => { 
                setShowFallecidaModal(false)
                setMascotaAccion(null)
                setFallecidaFormData({ fecha_fallecimiento: '', motivo_fallecimiento: '' })
                setProcessingAction(false)
              }}
              disabled={processingAction}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              variant="danger" 
              loading={processingAction}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  )
}
